import { useMemo } from 'react'
import { Box, Tooltip } from '@mui/material'

// Simple SVG sparkline component
export function Sparkline({
  data,
  width = 60,
  height = 20,
  color = '#7CB342',
  showDots = false,
  strokeWidth = 1.5
}) {
  const pathData = useMemo(() => {
    if (!data || data.length < 2) return null

    const min = Math.min(...data)
    const max = Math.max(...data)
    const range = max - min || 1

    const points = data.map((value, index) => {
      const x = (index / (data.length - 1)) * width
      const y = height - ((value - min) / range) * (height - 4) - 2
      return { x, y, value }
    })

    const pathCommands = points.map((point, i) =>
      i === 0 ? `M ${point.x} ${point.y}` : `L ${point.x} ${point.y}`
    ).join(' ')

    return { pathCommands, points }
  }, [data, width, height])

  if (!pathData) {
    return <Box sx={{ width, height, bgcolor: 'grey.100', borderRadius: 0.5 }} />
  }

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <path
        d={pathData.pathCommands}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {showDots && pathData.points.map((point, i) => (
        <circle
          key={i}
          cx={point.x}
          cy={point.y}
          r={2}
          fill={color}
        />
      ))}
    </svg>
  )
}

// Sparkline with before/after comparison
export function ComparisonSparkline({
  before,
  after,
  width = 50,
  height = 24,
  label
}) {
  const improvement = before && after ? ((before - after) / before * 100).toFixed(1) : null
  const isImprovement = improvement > 0

  const tooltipContent = label || (
    `kW/ton: ${before?.toFixed(2)} → ${after?.toFixed(2)} (${isImprovement ? '-' : '+'}${Math.abs(improvement)}% reduction)\n` +
    `Lower kW/ton = more efficient. This is the standard M&V metric for power consumption per ton of cooling.`
  )

  return (
    <Tooltip
      title={tooltipContent}
      placement="top"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <svg width={width} height={height}>
          {/* Before bar */}
          <rect
            x={0}
            y={height / 2 - 6}
            width={width * 0.4}
            height={12}
            fill="#ef5350"
            rx={2}
            opacity={0.8}
          />
          {/* After bar (scaled relative to before) */}
          <rect
            x={width * 0.5}
            y={height / 2 - 6}
            width={after && before ? (width * 0.4 * (after / before)) : width * 0.3}
            height={12}
            fill="#66bb6a"
            rx={2}
            opacity={0.8}
          />
          {/* Arrow */}
          <path
            d={`M ${width * 0.42} ${height / 2} L ${width * 0.48} ${height / 2}`}
            stroke="#666"
            strokeWidth={1.5}
            markerEnd="url(#arrowhead)"
          />
          <defs>
            <marker
              id="arrowhead"
              markerWidth="6"
              markerHeight="6"
              refX="5"
              refY="3"
              orient="auto"
            >
              <polygon points="0 0, 6 3, 0 6" fill="#666" />
            </marker>
          </defs>
        </svg>
      </Box>
    </Tooltip>
  )
}

// Mini bar chart for distribution
export function MiniBarChart({
  data,
  width = 60,
  height = 20,
  color = '#7CB342',
  gap = 1
}) {
  if (!data || data.length === 0) return null

  const max = Math.max(...data)
  const barWidth = (width - (data.length - 1) * gap) / data.length

  return (
    <svg width={width} height={height}>
      {data.map((value, i) => {
        const barHeight = (value / max) * (height - 2)
        return (
          <rect
            key={i}
            x={i * (barWidth + gap)}
            y={height - barHeight - 1}
            width={barWidth}
            height={barHeight}
            fill={color}
            opacity={0.8}
            rx={1}
          />
        )
      })}
    </svg>
  )
}

// Efficiency indicator with color gradient
export function EfficiencyIndicator({ value, width = 40, height = 8 }) {
  if (value == null) return null

  // Color based on efficiency: red < 15%, yellow 15-25%, green > 25%
  const getColor = (val) => {
    if (val < 15) return '#ef5350'
    if (val < 25) return '#ffb74d'
    return '#66bb6a'
  }

  const fillWidth = Math.min(100, Math.max(0, value)) / 100 * width

  const tooltipText = `${value.toFixed(1)}% kW/ton improvement (calculated from measured before/after values). ` +
    `Lower kW/ton = more efficient cooling per unit of power.`

  return (
    <Tooltip title={tooltipText}>
      <svg width={width} height={height} style={{ display: 'block' }}>
        {/* Background */}
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill="#e0e0e0"
          rx={height / 2}
        />
        {/* Fill */}
        <rect
          x={0}
          y={0}
          width={fillWidth}
          height={height}
          fill={getColor(value)}
          rx={height / 2}
        />
      </svg>
    </Tooltip>
  )
}
